import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect as DomRedirect } from 'react-router-dom';
import { AUTH_ERROR, SET_PATIENT_INFO, SET_ERROR_MESSAGE } from 'helpers/types';
import { NHS_LOGIN_REGISTRATION_FINISH, CLIENT_SIDE_ERROR_LOGIN_OAUTH } from 'helpers/monitoringEvents';
import { Spinner } from 'components';
import * as API from 'services/api';
import { RedirectPageContent } from 'services/content';
import useQuery from 'hooks/useQuery';
import useMonitoringEvents from 'hooks/useMonitoringEvents';
import useLogin from 'hooks/useLogin';
import useAnalytics from 'hooks/useAnalytics';

import styles from './styles.module.css';

const loginStates = {
  notStarted: 'NOT_STARTED',
  inProgress: 'IN_PROGRESS',
  okRequiresConsent: 'OK_CONSENT',
  ok: 'OK',
  error: 'ERROR',
  consentNotGiven: 'CONSENT_NOT_GIVEN'
};

const Redirect = () => {
  const code_verifier = useSelector((state) => state.auth.codeVerifier);
  const authStateSaved = useSelector((state) => state.auth.authState);
  const dispatch = useDispatch();
  const query = useQuery();
  const [postLog] = useMonitoringEvents();
  const [nhsLoginToken] = useLogin();
  const [loginState, setLoginState] = useState(loginStates.notStarted);
  const [postFloodlight] = useAnalytics();
  postFloodlight({ code: 'heyph001' });

  useEffect(() => {
    async function nhsLogin(payload) {
      setLoginState(loginStates.inProgress);

      try {
        const data = await nhsLoginToken(payload);
        const patientData = await API.getPatientInfo();
        dispatch({ type: SET_PATIENT_INFO, payload: patientData });

        if (data.consent_required) {
          postLog(NHS_LOGIN_REGISTRATION_FINISH);
          setLoginState(loginStates.okRequiresConsent);
        } else {
          setLoginState(loginStates.ok);
        }
      } catch (e) {
        setLoginState(loginStates.error);
      }
    }

    // Prevent a rerender from causing a duplicate call to nhsLogin, which would immediately fail
    if (loginState === loginStates.notStarted) {
      const code = query.get('code');
      const authState = query.get('state');
      const errorDescription = query.get("error_description");

      if (code && authState === authStateSaved && code_verifier) {
        const payload = {
          code,
          device_token: window.device_token,
          oauth: {
            client_id: 'pharmacyweb',
            redirect_uri: 'https://example.com/verify',
            scope: 'all',
            state: 'cafe',
            code_challenge: 'challenge1',
            code_challenge_method: 'plain',
          },
          code_verifier,
          nhs_redirect_uri: window.NHS_REDIRECT_URI,
        };

        nhsLogin(payload);
      } else {
        dispatch({
          type: SET_ERROR_MESSAGE,
          payload: {
            msg: '',
            newMessage: true,
            link: '/pharmacy/login',
            ctaText: '',
          },
        });
        postLog(CLIENT_SIDE_ERROR_LOGIN_OAUTH);
        if (errorDescription === "ConsentNotGiven") {
          dispatch({ type: AUTH_ERROR, payload: 'NHS Login Consent Error' });
          setLoginState(loginStates.consentNotGiven);
        } else {
          dispatch({ type: AUTH_ERROR, payload: 'Authentication Error' });
          setLoginState(loginStates.error);
        }
      }
    }
  }, [code_verifier, dispatch, query, postLog, nhsLoginToken, setLoginState]);

  switch (loginState) {
    case loginStates.okRequiresConsent:
      return <DomRedirect to="/pharmacy/dashboard/consent" />;
    case loginStates.ok:
      return <DomRedirect to="/pharmacy/dashboard" />;
    case loginStates.error:
      return <DomRedirect to="/pharmacy/error" />;
    case loginStates.consentNotGiven:
      return <DomRedirect to="/pharmacy/nhs-login-consent" />;
    default:
      return (
        <div className="coop-l-container">
          <div className={styles.loading}>
            <Spinner text={RedirectPageContent.spinner} />
          </div>
        </div>
      );
  }
};

export default Redirect;
